(ns minorodata.components.transformations.schema-tree
  (:require
   ["lucide-react" :refer [ArrowDownAZ ChevronRight Ellipsis]]
   ["react" :as react]
   [applied-science.js-interop :as js-interop]
   [clojure.set :as c.set]
   [clojure.string :as str]
   [minorodata.app.handlers :refer [dispatch dispatch-sync]]
   [minorodata.components.spinner.core :as c.spinner]
   [minorodata.json-flattening.model :refer [->json-flattening]]
   [minorodata.utils :as utils]
   [re-posh.core :as re-posh]
   [reagent.core :as reagent]
   [ui.button]
   [ui.chip]
   [ui.dropdown-menu]
   [ui.fields]
   [ui.table :as table]
   [ui.tooltip]
   [ui.typography]
   [ui.utils :refer [clsx]]))

(defn replace-default-node [structure]
  ;; The root node in the sql structure response of the current implementation
  ;; is a single node that has the table name you selected. Weird, but ok. This
  ;; results in first level of the structure view always being collapsed.
  ;;
  ;; This is obviously not what we want.
  ;;
  ;; So I'm going to remove the root node and make its immediate children the
  ;; root nodes.
  (let [columns (:json-structure structure)
        ;; root node doesn't have a parent
        is-root-node? #(= "" (get-in % [:relationships :parent-node]))
        default-node (first (filter is-root-node? columns))
        immediate-children (get-in default-node [:relationships :child-nodes])

        ;; Make all immediate children of the default node root nodes
        updated-columns (map (fn [column]
                               (if (some #{(:key-id column)} immediate-children)
                                 (assoc-in column [:relationships :is-root-node] true)
                                 column))
                             columns)


        ;; And remove the default node entirely so it doesn't confuse people
        without-default-node (vec (disj (set updated-columns) default-node))]

    (assoc structure :json-structure without-default-node)))

(defn compute-sort-order
  "Returns a vector of sort order position, starting from the root node"
  [node structure]
  (let [is-root-node? (get-in node [:relationships :is-root-node])
        read-order (:read-order node)
        parent-node-id (get-in node [:relationships :parent-node])
        parent-node (utils/find-by-field structure :key-id parent-node-id)]
    (if is-root-node?
      [read-order]
      (vec (flatten [(compute-sort-order parent-node structure)
                     read-order])))))


(defn normalize-vector-size
  "Make a vector of a given size, fill the rest with placeholders"
  ([v size] (normalize-vector-size v size 0))
  ([v size placeholder]
   (vec (take size (concat v (repeat size placeholder))))))

(defn sort-by-read-order'
  "Recursively sorts :json-structure vector by :read-order"
  [nodes]
  (let [structure @(re-posh/subscribe [:json-flattening/prop :json-flattening/structure])
        structure (replace-default-node structure)
        structure (:json-structure structure)

        sort-order-cache (reduce (fn [acc node]
                                   (assoc acc node (compute-sort-order node structure)))
                                 {}
                                 nodes)
        max-len (or (->> (vals sort-order-cache)
                         (map count)
                         (apply max))
                    ;; just in case
                    10)

        sort-order-cache-normalized (into {} (map (fn [[k v]]
                                                    [k (normalize-vector-size v max-len)])
                                                  sort-order-cache))]
    (sort-by sort-order-cache-normalized nodes)))

(def sort-by-read-order (memoize sort-by-read-order'))

(defn sort-by-name' [nodes]
  (sort-by :key-name nodes))

(def sort-by-name (memoize sort-by-name'))

(defmulti tree-node (fn [_ _ node] (if (get-in node [:relationships :is-branch-node])
                                       :json
                                       :column)))

(defn request-new-preview
  ([] (request-new-preview {}))
  ([configuration-cache]
   (let [ordered-columns @(re-posh/subscribe [:json-flattening/prop :json-flattening/ordered-columns])
         structure @(re-posh/subscribe [:json-flattening/prop :json-flattening/structure])]

     (dispatch [:json-flattening.api/fetch-sql {:columns ordered-columns
                                                :config   configuration-cache
                                                :structure structure}]))))


(defn get-children [nodes parent-node-id]
  (filter (comp (partial = parent-node-id) :parent-node :relationships) nodes))

(defn get-nested-children [nodes parent-node-id]
  (let [children (get-children nodes parent-node-id)]
    (when-not (empty? children)
      (concat children
              (mapcat (fn [child]
                        (get-nested-children nodes (get child :key-id)))
                      children)))))

(defn simple-data-preview [{:keys [data]}]
  [:div
   [table/table
    [table/thead
     [table/tr
      [table/th "Data preview"]]]

    (if (not-empty data)
      (into [table/tbody]
            (map (fn [v]
                   [table/tr {:class "h-auto"}
                    [table/td (cond
                                (nil? v) "null"
                                (= "" v) "null"
                                :else v)]])
                 data))
      [table/tbody
       [table/tr {:class "h-auto"}
        [table/td "No data"]]])]])

(defn generic-tree-node [params]
  (let [{:keys [type node nodes selected-nodes ordered-columns sort-alphabetically search-matches]} params

        {:keys [key-name key-id data-type is-pk relationships data-sample]} node
        {:keys [is-root-node is-branch-node parent-node child-nodes]} relationships

        selected-columns @(re-posh/subscribe [:json-flattening/prop :json-flattening/selected-columns])
        checked-columns  @(re-posh/subscribe [:json-flattening/prop :json-flattening/checked-columns])
        ordered-columns  @(re-posh/subscribe [:json-flattening/prop :json-flattening/ordered-columns])

        selected? (some #{key-id} selected-columns)
        checked? (some #{key-id} checked-columns)
        ordered? (some #{key-id} ordered-columns)

        removed? (and ordered? (not checked?))

        check (fn [e]
                (let [next-checked? e
                      checked-columns @(re-posh/subscribe [:json-flattening/prop :json-flattening/checked-columns])
                      checked-columns' (vec (if next-checked?
                                              (distinct (conj checked-columns key-id))
                                              (disj (set checked-columns) key-id)))]
                  (dispatch [:json-flattening/update (->json-flattening {:checked-columns checked-columns'})])))


        force-check (fn []
                      (let [checked-columns @(re-posh/subscribe [:json-flattening/prop :json-flattening/checked-columns])
                            checked-columns' (vec (if checked?
                                                    (disj (set checked-columns) key-id)
                                                    (distinct (conj checked-columns key-id))))]
                        (dispatch [:json-flattening/update (->json-flattening {:checked-columns checked-columns'})])))

        select (fn [e]
                 (js-interop/call e :stopPropagation)
                 (let [selected-columns @(re-posh/subscribe [:json-flattening/prop :json-flattening/selected-columns])
                       selected? (some #{key-id} selected-columns)
                       selected-columns' (vec (if selected?
                                                (disj (set selected-columns) key-id)
                                                (distinct (conj selected-columns key-id))))]
                   (dispatch [:json-flattening/update (->json-flattening {:selected-columns selected-columns'})])))

        empty-select (fn [e]
                       (js-interop/call e :stopPropagation))

        open?     selected?

        maybe-show-node (fn [node]
                          (if (empty? search-matches)
                            node
                            (let [all-matched-parent-nodes (set (map #(get-in % [:relationships :parent-node]) search-matches))]
                              (or
                               (contains? all-matched-parent-nodes (:key-id node))
                               (contains? search-matches node)))))

        sort-structure (fn [structure]
                         (if sort-alphabetically
                           (sort-by-name structure)
                           (sort-by-read-order structure)))

        children (->> (get-children nodes key-id)
                      (filter maybe-show-node)
                      (sort-structure))

        all-nested-children (get-nested-children nodes key-id)
        all-nested-children-ids (map :key-id all-nested-children)

        some-children-checked? (some (set all-nested-children-ids) (set checked-columns))

        *-chcked? (and (not-empty all-nested-children-ids)
                       (c.set/subset? (set all-nested-children-ids) (set checked-columns)))

        check-json (fn [e]
                     (let [next-checked?    e
                           checked-columns  @(re-posh/subscribe [:json-flattening/prop :json-flattening/checked-columns])
                           checked-columns' (vec (if next-checked?
                                                   (distinct (concat checked-columns all-nested-children-ids))
                                                   (c.set/difference (set checked-columns) (set all-nested-children-ids))))]
                       (dispatch [:json-flattening/update (->json-flattening {:checked-columns checked-columns'})])))

        json? is-branch-node]


    [:div {:class "relative cursor-pointer"
           :on-click (if json? select empty-select)}

     [ui.tooltip/tooltip {:trigger-as-child true
                          :class "max-w-radix-tooltip-content-available max-h-radix-tooltip-content-available overflow-auto z-2200"
                          :content-props {:side "right"}
                          :content [:f> simple-data-preview {:data data-sample}]}
      [:div {:class "flex justify-between items-center w-full overflow-hidden group hover:bg-kleene-hover-background transition-colors py-0.5 pr-1"}

       (if json?
         [:> ChevronRight {:class (clsx "leading-zero align-top text-kleene-icon shrink-0" (when open? "rotate-90"))
                           :size 20}]
         [:div {:class "grow-0 shrink-0 h-5 w-5"}])


       [:span {:on-click (fn [e]
                           (js-interop/call e :stopPropagation))
               :class "mr-2"}
        [ui.fields/checkbox
         {:id key-id
          :on-change (fn [e]
                       (if json?
                         (check-json e)
                         (check e)))

          :class (cond
                   removed? (if checked?
                              "bg-kleene-secondary-02 border-kleene-secondary-02 opacity-30"
                              "bg-kleene-error-05 border-kleene-error-01")

                   ordered? (if checked?
                              "bg-kleene-secondary-02 border-kleene-secondary-02 opacity-30"
                              "")
                   :default "")

          :checked (if json?
                     (if (and some-children-checked?
                              (not *-chcked?))
                       "indeterminate"
                       *-chcked?)
                     checked?)}]]

       [:div {:class "flex gap-1 flex-row grow shrink items-center overflow-hidden"}
        [:label {:html-for key-id
                 :class "mr-1 inline-block leading-5 truncate"}
         key-name]

        (when json?
          [ui.dropdown-menu/dropdown-menu
           {:trigger-as-child false
            :root-props   {:modal false}
            :content [:div
                      [ui.dropdown-menu/dropdown-menu-item {:on-click (fn [e]
                                                                        (js-interop/call e :stopPropagation)
                                                                        (force-check))}
                       (if checked?
                         "Do not include as a stringified JSON object"
                         "Include as a stringified JSON object")]]}

           [ui.button/button {:size :icon-xs
                              :icon Ellipsis
                              :component :div
                              :class "py-0.5"
                              :on-click #(js-interop/call % :stopPropagation)
                              :variant (if checked? :primary :secondary)}]])]

       (when (some? data-type)
         [ui.chip/chip {} data-type])

       (when is-pk
         [ui.chip/chip {} "PK"])]]

     (when json?
       (when open?
         (into [:div {:style {:margin-left "28px"}}]
               (map-indexed (partial tree-node {:nodes nodes
                                                :selected-nodes selected-nodes
                                                :ordered-columns ordered-columns
                                                :sort-alphabetically sort-alphabetically
                                                :search-matches search-matches})
                            children))))]))

(defn title-sorting-fn [title-key]
  (fn [a-node b-node]
    (let [a-title (js-interop/get a-node title-key)
          b-title (js-interop/get b-node title-key)]
      (compare a-title b-title))))

(defn max-if-sql-max-varchar-size
  "Print \"MAX\" if length is 65535 (redshift limit) or 16777216 (snoflake limit). Otherwise print length."
  [len]
  (if (or (= 65535 len)
          (= 16777216 len))
    "MAX"
    (str len)))


(defn column-type-name [node]
  (let [type-name (js-interop/get node :type-name)
        char-octet-length (js-interop/get node :char-octet-length)]
    (if (and (= "VARCHAR" type-name)
             (some? char-octet-length))
      (str type-name " (" (max-if-sql-max-varchar-size char-octet-length) ")")
      type-name)))


(defmethod tree-node :column [{:keys [nodes selected-nodes ordered-columns sort-alphabetically search-matches]} idx node]
  (let [{:keys [key-name key-id data-type is-pk relationships]} node
        {:keys [is-root-node is-branch-node parent-node child-nodes]} relationships]

    ^{:key (str key-id)}
    [generic-tree-node {:type :column
                        :node node
                        :nodes nodes
                        :selected-nodes selected-nodes
                        :ordered-columns ordered-columns
                        :sort-alphabetically sort-alphabetically
                        :search-matches search-matches}]))


(defmethod tree-node :json [{:keys [nodes selected-nodes ordered-columns sort-alphabetically search-matches]} idx node]
  (let [{:keys [key-name key-id data-type is-pk relationships]} node
        {:keys [is-root-node is-branch-node parent-node child-nodes]} relationships]

    [generic-tree-node {:type :json
                        :node node
                        :nodes nodes
                        :selected-nodes selected-nodes
                        :ordered-columns ordered-columns
                        :sort-alphabetically sort-alphabetically
                        :search-matches search-matches}]))


(defmethod tree-node :default [_ idx node]
  [:div {:class (clsx "relative")}
   [:div {:class "flex items-center pr-1 transition-colors cursor-pointer"}

    [:header {:class "flex justify-between items-center w-full overflow-hidden group text-kleene-text"}
     [:div {:class "flex items-center overflow-hidden"}
      [:> ChevronRight {:class (clsx "leading-zero align-top text-kleene-icon shrink-0") :size 20}]

      [:span {:class (clsx "whitespace-nowrap overflow-hidden text-ellipsis group-hover:opacity-60 transition-opacity")
              :title (str "default-" idx)}
       (str node)]]
     [ui.typography/small {:class "whitespace-nowrap text-[10px] text-kleene-dark-05 dark:text-kleene-neutral-01"} "Unknown"]]]])


(def scroll-position-cache
  (reagent/atom {:position 0
                 :key      nil}))

(defn tree [{:keys [selected-columns structure ordered-columns sort-alphabetically search-matches]}]
  (let [scrollable-portion-ref (react/useRef nil)


        nodes (:json-structure structure)
        is-root-node? (fn [node] (get-in node [:relationships :is-root-node]))

        sort-structure (fn [structure]
                         (if sort-alphabetically
                           (sort-by-name structure)
                           (sort-by-read-order structure)))

        maybe-show-node (fn [node]
                          (if (empty? search-matches)
                            node
                            (let [all-matched-parent-nodes (set (map #(get-in % [:relationships :parent-node]) search-matches))]
                              (or
                               (contains? all-matched-parent-nodes (:key-id node))
                               (contains? search-matches node)))))

        just-top-level-nodes (->> (filter is-root-node? nodes)
                                  (filter maybe-show-node)
                                  sort-structure)]

    (react/useEffect
     (fn []
       (when (and (.-current scrollable-portion-ref)
                  (= structure (:key @scroll-position-cache)))
         (-> scrollable-portion-ref
             (.-current)
             (js-interop/call :scroll 0 (:position @scroll-position-cache))))

       (fn []))
     #js [])

    [:div {:class "h-full overflow-y-scroll overflow-x-hidden grow shrink"
           :ref scrollable-portion-ref
           :on-scroll (fn [evt]
                        (let [position (js-interop/get-in evt [:target :scrollTop])]
                          (reset! scroll-position-cache
                                  {:key      structure
                                   :position position})))}
     (into
      [:div {:class "h-full"}]
      (map-indexed (partial tree-node {:nodes nodes
                                       :selected-nodes selected-columns
                                       :ordered-columns ordered-columns
                                       :sort-alphabetically sort-alphabetically
                                       :search-matches search-matches})
                   just-top-level-nodes))]))

(defn schema-tree [{:keys [controls]}]
  (let [columns-loading?  @(re-posh/subscribe [:mold-http/loading? :json-flattening.api/fetch-columns])
        selected-columns @(re-posh/subscribe [:json-flattening/prop :json-flattening/selected-columns])
        checked-columns @(re-posh/subscribe [:json-flattening/prop :json-flattening/checked-columns])
        ordered-columns @(re-posh/subscribe [:json-flattening/prop :json-flattening/ordered-columns])
        structure @(re-posh/subscribe [:json-flattening/prop :json-flattening/structure])
        structure (replace-default-node structure)

        [sort-alphabetically set-sort-alphabetically] (react/useState false)
        [search-term set-search-term] (react/useState "")
        [search-matches set-search-matches] (react/useState [])

        all-columns (->> (:json-structure structure)
                         (sort-by-read-order)
                         (mapv :key-id))
        all-columns-checked? (= (set all-columns) (set checked-columns))]

    (react/useEffect
     (fn []
       (if (str/blank? search-term)
         (set-search-matches #{})
         (let [nodes (:json-structure structure)
               matches (filter (fn [node] (re-matches (re-pattern (str "(?i).*" search-term ".*"))
                                                       (:key-name node)))
                                nodes)]
           (set-search-matches (set matches))))
       (fn []))
     #js [search-term])


    [:div {:class "overflow-hidden h-full shrink grow flex flex-col"}
     [:div {:class "flex items-center mb-2 gap-1"}
      [ui.typography/h3 {:class "grow"}
       "Fields"]

      (when-not columns-loading?
        [ui.button/button {:size     :micro
                           :variant  :secondary
                           :disabled all-columns-checked?
                           :on-click (fn select-all []
                                       (dispatch-sync [:json-flattening/update (->json-flattening {:checked-columns all-columns})]))}
         "Select all"])

      (when-not columns-loading?
        [ui.button/button {:size     :micro
                           :variant  :secondary
                           :disabled (empty? checked-columns)
                           :on-click (fn deselect-all []
                                       (dispatch-sync [:json-flattening/update (->json-flattening {:checked-columns []})]))}
         "Clear"])]

     (if columns-loading?
       [:div {:class "h-full w-full flex items-center justify-center"}
        [c.spinner/inline-configurable {:color :primary
                                        :size 50}]]

       [:div {:class "overflow-hidden flex flex-col grow border rounded-lg cursor-pointer"}
        (when controls
          [:div {:class "flex flex-row gap-1 border-b p-2"}
           [ui.fields/input
            {:field        {:value  search-term
                            :action (fn [e]
                                      (set-search-term (js-interop/get-in e [:target :value])))}

             :type "search"
             :placeholder  "Filter"
             :input-props {:on-blur (fn [])}
             :id "column-search"
             :required true
             :class      ""
             :full-width true
             :native     false}]

           [ui.button/button {:size     :icon
                              :icon     ArrowDownAZ
                              :variant  (if sort-alphabetically :primary-border :secondary)
                              :title    "Sort alphabetically"
                              :on-click (fn toggle-sort []
                                          (set-sort-alphabetically (not sort-alphabetically)))}]])

        [:f> tree {:structure           structure
                   :sort-alphabetically sort-alphabetically
                   :search-matches      search-matches
                   :selected-columns    selected-columns
                   :ordered-columns     ordered-columns}]])]))
